import React, { useMemo, useEffect } from "react";
import { Row, Col, Image, Collapse } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";

import {
  AssetCard,
  AssetUploadCard,
  CleverstoryAssetCard,
  LinkAssetCard,
} from "src/components/asset/add/card";
import RecommendedAssets from "../recommendedAssets";
import { fetchContentRecommendations } from "src/redux/actions/assets";
import { fetchCollection } from "src/redux/actions/collections";
import AdvancedSettings from "../advancedSettings";
import {AssetUpload} from 'src/types/assets'
import {Asset} from 'src/types/assets'
import pluralize from 'pluralize';

import _ from "lodash";
import cx from "classnames";
import styles from "./configurations.module.scss";

const NoAssetSelected = "/images/assets/no_assets_selected.svg";
const DefaultCollectionIcon = "/images/collections/background.jpeg";

interface Entity {
  id: string;
  entityType?: string;
  name?: string;
  sections?: any[];
  entities?: any[];
  assets?: any[];
  url?: string;
  item?: any;
  banner?: {
    background?: Background;
  };
}

interface Background {
  type: "image" | "video";
  full?: string; 
  thumbnail?: string; 
}

interface AssetEntity {
  id: string;
  entityType: string;
}

interface EmailConfig {
  addAsset: {
    option: any | null;
    entities: AssetEntity[];
  };
}

interface EmailRequest {
  assets: string[];
  canDownload: boolean;
  canReshare: boolean;
  downloadEditable: boolean;
  enabledBulkEmail: boolean;
  engage: boolean;
  expiryDate: any;
  nda: any;
  password: any;
}

interface EmailConfigurationProps {
  className: string;
  entities: Entity[];
  onAssetRemove: (asset: any) => void;
  onToggle: (key: string, value?: any) => void
  onSelection : (key: string, value?: any) => void
  emailRequestState: EmailRequest;
  emailConfigState: EmailConfig
  onAssetAdd : (asset:any) => void;
  isSidePanelOpen : boolean;
  dialog : boolean;
  advancedSettings : boolean;
  handleAdvancedSettings: () => void;
  handleRecommendedAssets : () => void;
  showRecommendedAssets : boolean;
  onClose: () => void;
  isDealRoom?: boolean;
}

const EmailConfiguration: React.FC<EmailConfigurationProps> = ({className,  entities = [], isDealRoom = false, onAssetRemove, onToggle , emailRequestState , onSelection , onAssetAdd , emailConfigState, isSidePanelOpen , dialog , advancedSettings , handleAdvancedSettings , handleRecommendedAssets , showRecommendedAssets, onClose}) => {

  const dispatch = useDispatch();

  const showEmptyMessage = entities.length === 0;

  const handleAssetRemove = (asset) => {
    onAssetRemove(asset)
    dispatch(fetchContentRecommendations([asset.id]));
  };

  const renderEntities = () => {
    return entities.map((entity) => {
      switch (entity.entityType) {
        case "asset-upload":
          return (
            <div className={styles.asset} key={entity.id}>
              <AssetUploadCard asset={entity as AssetUpload} onRemove={handleAssetRemove} />
            </div>
          );
        case "asset":
          return (
            <div className={styles.asset} key={entity.id}>
              <AssetCard asset={entity as Asset} editable={true} onRemove={handleAssetRemove} />
            </div>
          );
          case "collection":
          return <CollectionCard key={entity.id} collection={entity} width={285} />;
        case "deal_room":
          return <DealRoomCard key={entity.id} dealRoom={entity} width={285} />;
        case "link":
          return (
            <div className={styles.asset} key={entity.id}>
              <LinkAssetCard url={entity.url} />
            </div>
          );
        case "cleverstory":
          return (
            <div className={styles.asset} key={entity.id}>
              <CleverstoryAssetCard page={entity.item} />
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <section className={cx(className, styles.configuration)}>
        <EntityHeader entities={entities} />

        {showEmptyMessage && (
          <div className={styles.subheading}>
            <div className={styles.emptyMessageCards}>
              <Image src={NoAssetSelected} />
            </div>
            <div className={styles.emptyMessageCards}>
              <span>No assets added</span>
            </div>
            {!dialog &&
              <div className={styles.emptyCard}>
              </div>
            }
          </div>
        )}
      <div className={cx(styles.scroll, { [styles.scrollList]: showRecommendedAssets && !advancedSettings })}>
        {!showEmptyMessage && (
          <AdvancedSettings
            onToggle={onToggle}
            emailRequestState={emailRequestState}
            onSelection={onSelection}
            advancedSettings={advancedSettings}
            handleAdvancedSettings={handleAdvancedSettings}
            onClose = {onClose}
            isDealRoom={isDealRoom}
          />
        )}
        <div className={cx(styles.assetScrollList, {[styles.list] : !showRecommendedAssets})}>
          <ul className={cx(styles.assets)}>{renderEntities()}</ul>
          </div>
        {(!isDealRoom && showEmptyMessage ||
          (emailConfigState.addAsset.entities.length > 0 &&
            emailConfigState.addAsset.entities[0].entityType !== 'collection'))  && !advancedSettings && !isDealRoom && (
              <RecommendedAssets
                onAssetAdd={onAssetAdd}
                emailConfigState={emailConfigState}
                isSidePanelOpen={isSidePanelOpen}
                dialog={dialog}
                showRecommendedAssets={showRecommendedAssets}
                handleRecommendedAssets={handleRecommendedAssets}
              />
          )}
        </div>
    </section>
  );
};

export default EmailConfiguration;

const EntityHeader = ({ entities}) => {
  const text = useMemo(() => {
    if(_.size(entities) === 1) {
      const entity = entities[0];
      if (entity.entityType === 'collection') {
        const sections = _.get(entity, "sections", []);
        const entities = _.get(entity, "entities", []);
        const assets = _.filter(entities, entity => _.get(entity, "type") === "ASSET" || _.get(entity, "entityType") === "asset");

        if (_.size(sections) > 0) {
          return (
            <>
              <span className={styles.collectionName}>{entity.name}</span>
              <span className={styles.sectionsCount}>
              / {_.size(sections)} {pluralize("Section", _.size(sections))}
              </span>
            </>
          );
        }

        return `${_.size(assets)} ${pluralize("Asset", _.size(assets))}`;
      } else if (entity.entityType === 'deal_room') {
        const sections = entity.sections || [];
        const assets = entity.assets || [];

        if (_.size(sections) > 0) {
          return (
            <>
              <span className={styles.collectionName}>{entity.name}</span>
              <span className={styles.sectionsCount}>
              / {_.size(sections)} {pluralize("Section", _.size(sections))}
              </span>
            </>
          );
        }

        return `${assets.length} ${pluralize("Asset", _.size(assets))}`;
      }
    }

    // count the assets in the collection
    let assetsCount = 0;
    entities.forEach((entity) => {
      if(entity.entityType === "collection") {
        assetsCount = _.size(entity.entities);
      }

      // asset uploads count
      if(_.includes(["asset", "asset-upload", "link", "cleverstory"], entity.entityType)) {
        assetsCount += 1;
      }
    })

    return `${assetsCount} ${pluralize('Asset', assetsCount)}`;
  }, [entities]);

  return (<h1 className={cx(styles.heading)}>{text}</h1>);
};

const DealRoomCard: React.FC<{ dealRoom: Entity; width: number }> = ({ dealRoom, width }) => {
  const assets = dealRoom.assets || [];

  return _.size(dealRoom.sections) > 0 ? (
    <div className={styles.asset}>
      <CollectionSectionCard collection={dealRoom} width={width} />
    </div>
  ) : (
    <>
      {assets.map((asset) => (
        <div className={styles.asset} key={asset.id}>
          <AssetCard asset={asset.asset} editable={false} onRemove={() => { }} />
        </div>
      ))}
    </>
  )
}

const CollectionCard: React.FC<{ collection: Entity; width: number }> = ({ collection, width }) => {

  const dispatch = useDispatch();
  const loading = useSelector((state) =>
    _.get(state, `collections.collection['${collection.id}'].loading`, false)
  );
  const loaded = useSelector((state) =>
    _.get(state, `collections.collection['${collection.id}'].loaded`, false)
  );

  const sections = _.get(collection, "sections", []);
  const entities = _.get(collection, "entities", []);
  const assets = _.filter(entities, entity => _.get(entity, "type") === "ASSET" || _.get(entity, "entityType") === "asset");

  useEffect(() => {
    if (!loading && !loaded) {
      dispatch(fetchCollection(collection.id));
    }
  }, [loading, loaded, collection.id, dispatch]);

  if (loading && !loaded) {
    return <div className={styles.collectionLoading}>Loading...</div>;
  }

  if (_.size(sections) > 0) {
    return (
      <div className={styles.asset}>
        <CollectionSectionCard collection={collection} width={width} />
      </div>
    );
  }

  if (_.size(assets) > 0) {
    return (
      <>
        {assets.map((assetObj) => {
          const assetData = assetObj.asset || assetObj;
          return (
            <div className={styles.asset} key={assetData.id}>
              <AssetCard asset={assetData} editable={false} onRemove={() => {}} />
            </div>
          );
        })}
      </>
    );
  }

  return (
    <div className={styles.collectionEmptyCards}>
      <div className={styles.subheading}>
        <div className={styles.emptyMessageCards}>
          <Image src={NoAssetSelected} />
        </div>
        <div className={styles.emptyMessageCards}>
          <span>No assets added</span>
        </div>
      </div>
    </div>
  );
};

const CollectionSectionCard: React.FC<{ collection: Entity; width: number }> = ({
  collection,
  width,
}) => {
  const firstLayerStyle = { width: width - 72, height: width * 0.6538 };
  const secondLayerStyle = { width: width - 32, height: width * 0.6338 };
  const iconStyle = { width, height: width * 0.6338 };
  const folderStyle = { height: width * 0.6338 + 10 };

  const icon = useMemo(() => {
    const background = _.get(collection, "banner.background") as Background | undefined;
  
    if (background?.type === "image") return background.full;
    if (background?.type === "video") return background.thumbnail;
  
    return DefaultCollectionIcon;
  }, [collection]);
  

  return (
    <div className={styles.collectionIcon} style={{ width }}>
      <div style={folderStyle} className={styles.folder}>
        <div style={firstLayerStyle} className={cx(styles.layer, styles.first)}>&nbsp;</div>
        <div style={secondLayerStyle} className={cx(styles.layer, styles.second)}>&nbsp;</div>
          <Image src={icon} style={iconStyle} className={styles.icon} />
        </div>
      </div>

  );
};
